import React, { useEffect, useRef, memo } from "react";

// TradingView widgets emit cross-origin "Script error." messages from inside their iframes.
// react-error-overlay in dev catches these and blocks the UI — we filter them out.
if (typeof window !== "undefined" && !window.__tvErrorFilterInstalled) {
  window.__tvErrorFilterInstalled = true;

  const isNoise = (msg) => {
    const m = String(msg || "");
    return m === "Script error." ||
           m.includes("ResizeObserver loop") ||
           m.includes("tradingview");
  };

  // 1) Suppress at the source
  const nativeOnError = window.onerror;
  window.onerror = function (msg, src, line, col, err) {
    if (isNoise(msg) || isNoise(src)) return true;
    return nativeOnError ? nativeOnError.apply(this, arguments) : false;
  };
  window.addEventListener("error", (e) => {
    if (isNoise(e.message) || isNoise(e.filename)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);
  window.addEventListener("unhandledrejection", (e) => {
    const msg = e.reason?.message || e.reason;
    if (isNoise(msg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);

  // 2) Hide the react-error-overlay iframe when it shows nothing but cross-origin noise.
  const removeOverlayIfNoise = () => {
    document.querySelectorAll(
      "iframe#webpack-dev-server-client-overlay, iframe[title='Uncaught runtime errors']"
    ).forEach((f) => {
      try {
        const body = f.contentDocument?.body?.innerText || "";
        if (body.includes("Script error.") && !body.match(/at\s+\w+\s+\(.*(?<!tradingview)\.js:/)) {
          f.remove();
        }
      } catch (_) { /* cross-origin */ }
    });
  };
  new MutationObserver(removeOverlayIfNoise).observe(document.documentElement, {
    childList: true, subtree: true,
  });
}

/**
 * TradingView Ticker Tape widget — real-time scrolling strip.
 * Streams tick-level data from TradingView (same rate as tradingview.com).
 * Gold-forward symbol list.
 */
export const TradingViewTape = memo(function TradingViewTape() {
  const container = useRef(null);
  useEffect(() => {
    if (!container.current) return;
    // Clear on re-mount (StrictMode / navigation)
    container.current.innerHTML = "";
    const wrapper = document.createElement("div");
    wrapper.className = "tradingview-widget-container__widget";
    container.current.appendChild(wrapper);

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js";
    script.async = true;
    script.type = "text/javascript";
    script.innerHTML = JSON.stringify({
      symbols: [
        { description: "Gold",   proName: "OANDA:XAUUSD" },
        { description: "Silver", proName: "OANDA:XAGUSD" },
        { description: "DXY",    proName: "TVC:DXY" },
        { description: "US 10Y", proName: "TVC:US10Y" },
        { description: "S&P",    proName: "CME_MINI:ES1!" },
        { description: "Nasdaq", proName: "CME_MINI:NQ1!" },
        { description: "Oil",    proName: "NYMEX:CL1!" },
        { description: "BTC",    proName: "BITSTAMP:BTCUSD" },
        { description: "EUR/USD", proName: "FX:EURUSD" },
        { description: "USD/JPY", proName: "FX:USDJPY" },
      ],
      showSymbolLogo: true,
      isTransparent: true,
      displayMode: "adaptive",
      colorTheme: "dark",
      locale: "en",
    });
    container.current.appendChild(script);
  }, []);
  return (
    <div className="tradingview-widget-container" ref={container} data-testid="tv-tape" />
  );
});

/**
 * TradingView Advanced Chart widget — real-time streaming chart with session overlays.
 *
 * Built-in studies enabled by default:
 *  - Sessions@tv-basicstudies         → Tokyo/London/NY session shading
 *  - PivotPointsHighLow@tv-basicstudies → PDH/PDL, weekly H/L, monthly H/L pivots
 *  - PivotPointsStandard@tv-basicstudies → standard daily/weekly pivot lines
 *
 * @param {string} symbol   TV symbol e.g. "OANDA:XAUUSD"
 * @param {string} interval "1","5","15","60","240","D","W"
 * @param {number|string} height  px or css value; ignored if `autosize`
 * @param {boolean} autosize   fill parent container
 * @param {string[]} extraStudies additional built-in studies to include
 */
export const TradingViewGoldChart = memo(function TradingViewGoldChart({
  symbol = "OANDA:XAUUSD",
  interval = "60",
  height = 480,
  autosize = true,
  extraStudies = [],
} = {}) {
  const container = useRef(null);
  useEffect(() => {
    if (!container.current) return;
    container.current.innerHTML = "";
    const wrapper = document.createElement("div");
    wrapper.className = "tradingview-widget-container__widget";
    wrapper.style.height = "100%";
    wrapper.style.width = "100%";
    container.current.appendChild(wrapper);

    // Studies — none pre-loaded. Users can add any indicator via the ƒx toolbar button.
    const studies = [...extraStudies];

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.async = true;
    script.type = "text/javascript";
    script.innerHTML = JSON.stringify({
      autosize,
      symbol,
      interval,
      timezone: "Etc/UTC",
      theme: "dark",
      style: "1",
      locale: "en",
      hide_side_toolbar: false,
      allow_symbol_change: true,
      calendar: false,
      studies,
      support_host: "https://www.tradingview.com",
      backgroundColor: "rgba(10, 10, 10, 1)",
      gridColor: "rgba(31, 31, 31, 1)",
    });
    container.current.appendChild(script);
  }, [symbol, interval, autosize, JSON.stringify(extraStudies)]);
  return (
    <div className="tradingview-widget-container"
         style={autosize ? { height: typeof height === "number" ? `${height}px` : height, width: "100%" } : {}}
         ref={container} data-testid={`tv-chart-${symbol}`} />
  );
});

/**
 * Compact single-symbol "Symbol Info" widget — used for the hero gold panel.
 * Shows big price, day change, key stats — updates on every tick.
 */
export const TradingViewSymbolInfo = memo(function TradingViewSymbolInfo({ symbol = "OANDA:XAUUSD" }) {
  const container = useRef(null);
  useEffect(() => {
    if (!container.current) return;
    container.current.innerHTML = "";
    const wrapper = document.createElement("div");
    wrapper.className = "tradingview-widget-container__widget";
    container.current.appendChild(wrapper);
    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-symbol-info.js";
    script.async = true;
    script.type = "text/javascript";
    script.innerHTML = JSON.stringify({
      symbol,
      width: "100%",
      locale: "en",
      colorTheme: "dark",
      isTransparent: true,
    });
    container.current.appendChild(script);
  }, [symbol]);
  return (
    <div className="tradingview-widget-container" ref={container} data-testid={`tv-symbol-${symbol}`} />
  );
});
